import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import BlockIcon from '@mui/icons-material/Block';
import {
  Box,
  TextField,
  IconButton,
  Alert,
  Button,
  CardContent,
  Card,
  Typography,
  useMediaQuery,
  useTheme,
  InputAdornment,
} from '@mui/material';
import { COLORS } from '../../theme/themeConstants';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { atom, useSetAtom } from 'jotai';
import {
  useAllStores,
  useDeleteStore,
  usePatchStore,
} from '../Query/ManageStoreQuery';
import SearchIcon from '@mui/icons-material/Search';
import { WritableRestaurantDataSchema } from '../../client-sdk/api';
import AddIcon from '@mui/icons-material/Add';
import EditStoreOwnerModal from './EditStoreOwnerModal';
import EditRestaurant from './EditRestaurant/EditRestaurant';
import SelectStore from './Store/AddStore';
import { useStoreManagementParams } from './StoreManagement/useStoreManagementParams';
import CommonDataGrid from '../Stateless/CommonDataGrid';
import ErrorHandler from '../Stateless/Common/ErrorHandler';
import { useDebounce } from './Utils/useDounce';
import PermissionActionButtons from '../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../context/PermissionContext';
import EditMenu from '@mui/icons-material/MoreVert';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useSnackbar } from '../Stateless/SnackBarProvider';

export const storeAtom = atom<any>(null);

type StoreWithSno = Partial<WritableRestaurantDataSchema> & {
  id: string;
  sno: number;
  name?: string;
  merchant?: string;
  merchantEmail?: string;
  category?: string;
  addedBy?: string;
  status?: string;
};

const ManageRestaurantTable = () => {
  const { showMessage } = useSnackbar();
  const setStoreAtom = useSetAtom(storeAtom);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { params, dispatch } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const { hasPermission } = usePermissions();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState('');
  const [snackbarSeverity] = useState<
    'success' | 'error' | 'info' | 'warning'
  >('info');
  const [modalContent] = useState<'add' | 'edit'>('edit');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const {
    data: manageStoreData,
    isLoading,
    refetch
  } = useAllStores(memoizedParams);

  const { mutate: patchStore } = usePatchStore();

  const { mutate: deleteStore } = useDeleteStore();

  // we'll render columns manually with permission-aware UI

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };

  const filteredRows = React.useMemo(() => {
    const storeOwners = manageStoreData?.data || [];
    // Add sno field to each row and ensure all required fields are present
    return storeOwners.map((row, index) => {
      // Ensure we have a valid row object
      if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

      // Create a new object with all the row properties plus sno
      const rowWithSno = {
        ...row,
        id: row.id,
        sno: index + 1 + (params.page - 1) * params.limit,
        name: row.nameEnglish,
        merchant: row.merchantName,
        merchantEmail: row.merchantEmail,
        storeType: row.restaurantDetails.storeType,
        addedBy: row.addedBy,
        status: row.status,
      };

      return {
        ...rowWithSno,
        storeType: rowWithSno.storeType as string,
      } as StoreWithSno;
    });
  }, [params.search, manageStoreData, params.page, params.limit]);

  // Add a console.log to verify the data:ws);

  const handleBlockClick = async () => {
    console.log('selectedRows', selectedRows);
    if (selectedRows.length === 0) {
      showMessage('Please select at least one store to block','error');
      
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      patchStore({
        id: selectedRows,
        data: { status: 'blocked' },
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one store to unblock','error');
      
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      patchStore({
        id: selectedRows,
        data: { status: 'unblocked' },
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    console.log('delete', selectedRows);
    if (selectedRows.length === 0) {
      showMessage('Select at least one store to delete','error');
      
    } else {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      // deleteStore(selectedRows);
      deleteStore(selectedRows, {
        onSuccess: () => {
          setSelectedRows([]);
          showMessage('store deleted successfully', 'success');

          // Refetch data to update the table
          refetch();
        },
        onError: () => {
          showMessage('Error deleting store', 'error');
          // If there's an error, we might want to restore the selection
          // setSelectedRows(selectedRows); // Uncomment if you want to restore selection on error
        },
      });
    }
  }, [selectedRows, deleteStore]);

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteStore([id]);
    },
    [selectedRows, deleteStore]
  );

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this store?'
    );
    if (!confirmed) return;

    await patchStore({
      id: [id],
      data: { status: 'blocked' },
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this store?'
    );
    if (!confirmed) return;

    await patchStore({
      id: [id],
      data: { status: 'unblocked' },
    });
  };

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
    dispatch({ type: 'SET_IS_EDIT', payload: false });
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setStoreAtom(null);
    dispatch({ type: 'SET_IS_EDIT', payload: false }); // SET TO FALSE
  };

  const handleEditClick = (row: any) => {
    setOpenEditDialog(true);
    console.log('rowssssssssssss', row);
    setStoreAtom(row);
    dispatch({ type: 'SET_STORE_TYPE', payload: row.storeType });
    dispatch({ type: 'SET_IS_EDIT', payload: true });
  };

  const MobileView = () => (
    <Box sx={{ mt: 2 }}>
      {filteredRows.map((row) => (
        <Card key={row.id} sx={{ mb: 2, border: '1px solid #e0e0e0' }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              {/* You can add a Checkbox for selection if needed */}
              <Typography variant="subtitle1" sx={{ ml: 1 }}>
                {row.name || '-'}
              </Typography>
            </Box>
            <Box sx={{ pl: 4 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Merchant:</strong> {row.merchant || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {row.merchantEmail || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Category:</strong> {row.category || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Status:</strong> {row.status || '-'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenEditDialog(true);
                    setStoreAtom(row);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    color: row.status === 'blocked' ? '#f44336' : '#4caf50',
                  }}
                  onClick={() =>
                    row.status === 'blocked'
                      ? handleUnBlockClickButton(row.id)
                      : handleBlockClickButton(row.id)
                  }
                >
                  {row.status === 'blocked' ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDeleteButton(row.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  // Compute which bulk actions to show based on selected rows' statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedRows || selectedRows.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = manageStoreData?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.id), String(item.status || '')])
    );
    const statuses = selectedRows.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return {
      showBlock: hasUnblocked,
      showUnblock: hasBlocked,
    };
  }, [selectedRows, manageStoreData]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('store', 'Edit');
    const canDelete = hasPermission('store', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <EditMenu fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditClick(row); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDeleteButton(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.3,
    },
    {
      field: 'name',
      headerName: 'Store Name',
      flex: 2,
    },
    {
      field: 'merchantName',
      headerName: 'Merchant Name',
      flex: 2,
    },
    {
      field: 'merchantEmail',
      headerName: 'Merchant Email',
      flex: 2,
    },
    {
      field: 'storeType',
      headerName: 'Store Type',
      flex: 1,
    },
    {
      field: 'addedBy',
      headerName: 'Added By',
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id;
        const validId = typeof id === 'string' ? id : '';
        const canBlock = hasPermission('store', 'Block');
        const canUnblock = hasPermission('store', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton
            size="small"
            sx={{ color: isBlocked ? '#f44336' : '#4caf50' }}
            onClick={() =>
              isBlocked
                ? handleUnBlockClickButton(validId)
                : handleBlockClickButton(validId)
            }
          >
            {isBlocked ? (
              <BlockIcon fontSize="small" />
            ) : (
              <CheckCircleIcon fontSize="small" />
            )}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  // Format the manageStoreData for the DataGrid

  return (
    <>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}
        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by Store Name, Merchant Name, Store Type, or Email"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                input: {
                  color: '#333', // Visible text color
                  '::placeholder': {
                    color: '#bdbdbd', // Visible placeholder color
                    opacity: 1, // Ensure it doesn't get hidden
                  },
                },
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  backgroundColor: '#fff', // Make sure input has white background
                },
                '& fieldset': {
                  border: 'none',
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <PermissionActionButtons
              module="store"
              onBlock={showBlock ? handleBlockClick : undefined}
              onUnblock={showUnblock ? handleUnBlockClick : undefined}
              onDelete={selectedRows.length ? handleDeleteClick : undefined}
            />
            {hasPermission('store', 'add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Store
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Store"
        >
          <SelectStore onCancel={handleCloseAddDialog} isEdit={params.isEdit} storeType={params.storeType} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={modalContent === 'add' ? 'Add Sub Admin' : 'Edit Store'}
        >
          {params.storeType === 'Restaurant' && (
            <EditRestaurant
              onCancel={handleCloseEditDialog}
              storeType={params.storeType}
              isEdit={params.isEdit}
            />
          )}

          {params.storeType === 'GroceryStore' && (
            <SelectStore onCancel={handleCloseAddDialog} isEditClose={handleCloseEditDialog} isEdit={params.isEdit} storeType={params.storeType} />
          )}

          {params.storeType === 'PharmacyStore' && (
            <SelectStore onCancel={handleCloseAddDialog} isEditClose={handleCloseEditDialog} isEdit={params.isEdit} storeType={params.storeType} />
          )}
        </EditStoreOwnerModal>

        {isMobile ? (
          <MobileView />
        ) : (
          <CommonDataGrid
            key={`datagrid-page-${params.page}-limit-${params.limit}`}
            rows={filteredRows || []}
            columns={columns}
            pageSize={params.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            checkboxSelection
            loading={isLoading}
            page={params.page - 1}
            rowCount={manageStoreData?.pagination?.totalItems}
            components={{
              NoRowsOverlay: () => <ErrorHandler type="no-data" message="No categories found" onRetry={() => refetch()} />
            }}
            onRowSelectionModelChange={(ids: string[]) => setSelectedRows(ids)}
            selectionModel={selectedRows}
          />
        )}
      </Box>
    </>
  );
};

export default ManageRestaurantTable;
